import { ERROR_CODES, NOTIFICATION_TEMPLATES } from '@iris/shared';

import ar from '../locales/ar.json';
import fr from '../locales/fr.json';

export const LOCALES = ['fr', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];

export const resources = { fr, ar } as const;

/** `{ a: { b: 'x' } }` → `['a.b']`. */
export function flattenKeys(tree: unknown, prefix = ''): string[] {
  if (typeof tree !== 'object' || tree === null) return [prefix];
  return Object.entries(tree).flatMap(([key, value]) =>
    flattenKeys(value, prefix ? `${prefix}.${key}` : key),
  );
}

/** Problems that fail `pnpm i18n:check`: keys missing in one locale, empty strings, untranslated error codes. */
export function findI18nProblems(locales: Record<Locale, unknown> = resources): string[] {
  const problems: string[] = [];
  const keys = Object.fromEntries(LOCALES.map((l) => [l, new Set(flattenKeys(locales[l]))])) as Record<
    Locale,
    Set<string>
  >;
  for (const locale of LOCALES) {
    for (const other of LOCALES) {
      if (other === locale) continue;
      for (const key of keys[other])
        if (!keys[locale].has(key)) problems.push(`${locale}: missing key "${key}"`);
    }
    for (const code of ERROR_CODES) {
      if (!keys[locale].has(`errors.${code}`))
        problems.push(`${locale}: missing error message errors.${code}`);
    }
    for (const template of NOTIFICATION_TEMPLATES) {
      for (const part of ['title', 'body']) {
        if (!keys[locale].has(`notifications.${template}.${part}`))
          problems.push(`${locale}: missing notification text notifications.${template}.${part}`);
      }
    }
  }
  const visit = (locale: Locale, tree: unknown, prefix: string) => {
    if (typeof tree === 'string') {
      if (tree.trim() === '') problems.push(`${locale}: empty string at "${prefix}"`);
      return;
    }
    if (typeof tree === 'object' && tree !== null) {
      for (const [k, v] of Object.entries(tree)) visit(locale, v, prefix ? `${prefix}.${k}` : k);
    }
  };
  for (const locale of LOCALES) visit(locale, locales[locale], '');
  return [...new Set(problems)];
}

export * from './errors';
export * from './messages';
export * from './setup';
